// https://www.figma.com/file/q7KlcLyYFswO2QB3I4BWmE/Interests-Graph-on-Web?node-id=4%3A2095
import { cx, css, useTheme } from '@fable/theme'
import { useNavigate, useLocation } from 'react-router-dom'
import { useActions, useTypedSelector } from '../../../../hooks'
import { FlexBox, Button, Text, Hyperlink } from '@fable/components'
import ShareButton from '../../../../components/share_button/ShareButton'
import MoreIconRound from '../../../../assets/icons/components/MoreIconRound'
import { CATEGORIES, EMAIL_REPORT_USER_LINK } from '../../../../constants'
import { FORM_TYPES } from '../../../auth/constants'
import alertIcon from '../../../../assets/icons/standard/alert-icon.svg'
import { getReferralId, isFalseyObject } from '../../../../utils'

const ProfileActions: React.VFC<React.HTMLProps<HTMLDivElement>> = () => {
  const { closeModal, openModal, deleteFollowUser, postFollowUser } =
    useActions()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { colors } = useTheme()
  const { openAuthModal, postAuthFollowUser } = useActions()
  const { profile } = useTypedSelector(({ user }) => user)
  const { isAuthenticated } = useTypedSelector(({ auth }) => auth)
  const { user } = useTypedSelector(({ social }) => social)
  const { id } = getReferralId({ enabled: true })
  const hasReferralId = !isAuthenticated && !!id
  const isFollowing = user.data?.is_following
  const loading = !isFalseyObject(user) && user.loading
  const loggedInUserId = profile.data?.id
  const viewedUserId = user.data?.id || ''
  const isViewingSelf = viewedUserId === loggedInUserId

  const promptAuthModal = (following: boolean = false) => {
    if (hasReferralId) {
      openAuthModal(FORM_TYPES.SIGN_UP)
      return
    }

    const postAuthFollowAction = following
      ? [postAuthFollowUser(viewedUserId)]
      : null

    openAuthModal(FORM_TYPES.SIGN_UP, pathname, postAuthFollowAction)
  }

  const handleClickFollow = () => {
    if (!isAuthenticated) {
      promptAuthModal(true)
      return
    }

    if (isFollowing) {
      deleteFollowUser(viewedUserId)
    } else {
      postFollowUser(viewedUserId)
    }
  }

  const moreButtonModalContent = () => (
    <div className="modal-content list">
      <Text
        className="modal-title"
        type="subhead"
        sizing={{ base: 'XS', mobileL: 'S' }}
        tag="h4"
      >
        Profile Options
      </Text>
      <Hyperlink
        className={cx(
          css`
            user-select: none;
            &:after {
              display: none;
            }
          `,
          'modal-item'
        )}
        href={EMAIL_REPORT_USER_LINK(loggedInUserId, viewedUserId)}
        onClick={() => closeModal()}
        newTab
      >
        <img src={alertIcon} alt="Alert. Click to report user." />
        <Text type="body" sizing="M">
          Report
        </Text>
      </Hyperlink>
    </div>
  )

  const handleClickMore = () => {
    if (!isAuthenticated) {
      promptAuthModal()
      return
    }
    openModal(moreButtonModalContent())
  }

  const followButtonLabel = () => {
    if (hasReferralId) return 'Sign up to Follow'

    if (isViewingSelf) return 'Edit'
    return isFollowing ? 'Following' : 'Follow'
  }

  return (
    <FlexBox centerAll>
      <ShareButton
        category={CATEGORIES.profile}
        className={css`
          background: ${colors.almostWhite};
          max-width: 32px;
          height: 32px;

          .fds-ui--icon {
            width: 22px;
          }
        `}
        negative
      />
      {/* only if public show follow button */}
      {!isViewingSelf && user.data?.private_profile ? (
        <div
          className={css`
            margin-right: 12px;
          `}
        />
      ) : (
        <Button
          width="200px"
          height="44px"
          className={css`
            margin: 0px 8px;
          `}
          loading={loading}
          onClick={
            isViewingSelf
              ? () =>
                  navigate('/store/profile', {
                    state: { triggerEdit: true },
                  })
              : handleClickFollow
          }
          label={followButtonLabel()}
          secondary={isFollowing}
          dataTestId="follow-button"
        />
      )}
      {isViewingSelf ? (
        <Button
          dataTestId="more-button"
          onClick={handleClickMore}
          negative
          className={css`
            width: fit-content;
            height: fit-content;
            padding: 0;
          `}
        >
          <MoreIconRound />
        </Button>
      ) : (
        <div
          className={css`
            width: 32px;
          `}
        />
      )}
    </FlexBox>
  )
}

export default ProfileActions
